import {
  BookOpenIcon, SignalIcon, BuildingOffice2Icon, TrophyIcon, ArrowTrendingUpIcon, ChartPieIcon,
  UserGroupIcon, AcademicCapIcon, CalendarDaysIcon, ExclamationTriangleIcon, CurrencyDollarIcon,
  ClockIcon, CalculatorIcon, Squares2X2Icon, PresentationChartLineIcon, TableCellsIcon,
  IdentificationIcon, Cog6ToothIcon, BuildingStorefrontIcon, DocumentMagnifyingGlassIcon, ChatBubbleBottomCenterTextIcon,
  SunIcon, CalendarIcon, ChartBarIcon, DocumentChartBarIcon, RocketLaunchIcon,
  BeakerIcon, FlagIcon, ScaleIcon, BoltIcon, PresentationChartBarIcon, ClipboardDocumentCheckIcon, MapPinIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { PERMISSIONS } from '../config/rolePermissions';
import { TOOL_KEYS } from '../config/toolAccess';
import { navVisible, visibleSections } from '../config/navVisibility';

/**
 * The Call Centre window's sidenav — every section of the module, gated by role.
 *
 * ── How visibility is decided ──────────────────────────────────────────────
 *
 * The whole window sits behind the Call Centre tool (`/call-centre` is a
 * `ToolRoute`), and every section says so itself with `toolKey` — a gate implied
 * only by where a link renders is one the drift test cannot see, and it failed on
 * exactly that. Anyone who holds the tool sees the Guide and My work — an
 * agent's own list, roster and figures. The sections that describe the DESK —
 * live board, performance, lead lists and set-up — need `VIEW_CALL_CENTRE_DESK`,
 * because they show every office and every lead. Two groups describe PEOPLE
 * and STAFFING — agent comparisons and planning — and need `VIEW_CALL_CENTRE_TEAM`
 * (admins always pass), which the API enforces on the same routes. The window is
 * independent of the Leads Tracker: no item links there. Every rule here uses
 * `navVisible`, the one filter the main sidebar
 * uses, and every restricted item has the SAME guard on its route in App.jsx —
 * `nav-route-drift.test.js` reads this file and fails if the two ever disagree.
 *
 * Role access to the tools themselves is edited at runtime in Settings → Tool
 * Access, so the menu follows that without a deploy.
 */
export const CALL_CENTRE_NAV = [
  {
    // First, so a new person meets the story before the numbers. It is the one
    // section that is not a data tab — it has its own page, `CallCentreGuide`.
    label: 'Start here',
    items: [
      { name: 'Guide', href: '/call-centre/guide', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: BookOpenIcon },
      /**
       * The presenting half of the Guide, and ungated on purpose.
       *
       * The Guide is a reference read alone; this is one idea at a time, big
       * enough for a room, with a line for whoever is speaking. Every audience
       * sees the same opening argument — giving an agent a different PROBLEM
       * from their team lead is how a desk ends up with two stories, and the
       * agent's is always the one that sounds like a rule handed down.
       *
       * The comment sits OUTSIDE the object: the nav guards parse this file as
       * text and a comment between `{` and `name:` makes an item invisible to
       * every one of them.
       */
      { name: 'Walkthrough', href: '/call-centre/demo', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: PresentationChartBarIcon },
    ],
  },
  {
    // The agent's own workspace. Tool only: the pages themselves say when a login
    // is not linked to an agent name, because the menu cannot know.
    label: 'My work',
    items: [
      { name: 'My Day', href: '/call-centre/my-day', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: SunIcon },
      { name: 'My Roster', href: '/call-centre/my-roster', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: CalendarIcon },
      { name: 'My Stats', href: '/call-centre/my-stats', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: ChartBarIcon },
      { name: 'Follow-up tasks', href: '/call-centre/my-followup', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: ClipboardDocumentCheckIcon },
    ],
  },
  {
    label: 'Live',
    items: [
      { name: 'Now', href: '/call-centre/now', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: SignalIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
    ],
  },
  {
    label: 'Performance',
    items: [
      { name: 'Offices', href: '/call-centre/offices', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: BuildingOffice2Icon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      { name: 'Scorecards', href: '/call-centre/scorecards', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: TrophyIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      { name: 'Trends', href: '/call-centre/trends', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: ArrowTrendingUpIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      { name: 'Outcome mix', href: '/call-centre/outcomes', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: ChartPieIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
    ],
  },
  {
    label: 'Leads',
    items: [
      { name: 'Exception lists', href: '/call-centre/exceptions', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: ExclamationTriangleIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      { name: 'What converts', href: '/call-centre/bookings', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: CurrencyDollarIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      { name: 'Coverage by hour', href: '/call-centre/coverage', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: ClockIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
    ],
  },
  {
    label: 'Team',
    items: [
      { name: 'Agents', href: '/call-centre/agents', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: UserGroupIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      { name: 'Efficiency & coaching', href: '/call-centre/efficiency', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: AcademicCapIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      { name: 'Day & week', href: '/call-centre/eod', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: CalendarDaysIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      { name: 'Reports', href: '/call-centre/reports', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: DocumentChartBarIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      { name: 'Boost bookings', href: '/call-centre/boost', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: RocketLaunchIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      /**
       * Who is working which leads, at which office. The Roster plans presence;
       * this is the other half — ownership — and until it existed every agent at
       * an office saw every lead at it.
       *
       * The comment sits OUTSIDE the object: the nav guards parse this file as
       * text and a comment between `{` and `name:` makes an item invisible to
       * every one of them.
       */
      { name: 'Lead ownership', href: '/call-centre/lead-ownership', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: ClipboardDocumentCheckIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
    ],
  },
  {
    label: 'Planning',
    items: [
      { name: 'Planning', href: '/call-centre/planning', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: CalculatorIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      { name: 'Capacity', href: '/call-centre/capacity', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: Squares2X2Icon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      { name: 'Forecast', href: '/call-centre/forecast', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: PresentationChartLineIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      { name: 'Roster locations', href: '/call-centre/roster-locations', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: MapPinIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      { name: 'Roster', href: '/call-centre/roster', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: TableCellsIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      { name: 'Simulation', href: '/call-centre/simulation', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: BeakerIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
      { name: 'Monthly plan', href: '/call-centre/cascade', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: FlagIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM },
    ],
  },
  {
    label: 'Setup',
    items: [
      // The desk permission to see; edits inside are MANAGE_CALL_CENTRE (directory) and MANAGE_SETTINGS (settings).
      /**
       * What the GHL team builds, per office. Beside Office set-up because the
       * practice details set there are what it renders with.
       */
      { name: 'GHL setup', href: '/call-centre/workflow', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: BoltIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      { name: 'Office set-up', href: '/call-centre/office-setup', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: BuildingStorefrontIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      /**
       * Call scripts and offers, per office. The team lead writes them; agents
       * read them on My Day before each call. Beside Office set-up because the
       * practice details there are what the scripts' {doctor}/{practice} fill from.
       */
      { name: 'Call scripts', href: '/call-centre/call-scripts', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: ChatBubbleBottomCenterTextIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      { name: 'Agent directory', href: '/call-centre/directory', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: IdentificationIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      { name: 'Settings', href: '/call-centre/settings', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: Cog6ToothIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      /**
       * Every other screen here assumes its data is live. This is the only one
       * that checks, and the only one that says what to do when it is not.
       * Desk-level rather than settings-level: the people who notice an office
       * is behind are the ones working it.
       */
      { name: 'Data freshness', href: '/call-centre/data-freshness', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: ShieldCheckIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      // The logic behind every figure — for whoever owns the thresholds (admins by default).
      /**
       * The remedy the "estimated" banner on every screen names. It was a
       * markdown file in the backend, which the person reading the banner cannot
       * open — so the banner named a fix nobody could follow.
       *
       * Desk-level, not settings-level: a team lead needs to understand why their
       * figures are estimated even though only an admin can record it.
       *
       * The comment sits OUTSIDE the object deliberately. The nav guards parse
       * this file as text and match `{ name: … }` with the two adjacent; a
       * comment between them makes the item invisible to every one of them.
       */
      { name: 'Calibration', href: '/call-centre/calibration', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: ScaleIcon, permission: PERMISSIONS.VIEW_CALL_CENTRE_DESK },
      { name: 'Methodology', href: '/call-centre/methodology', toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL, icon: DocumentMagnifyingGlassIcon, permission: PERMISSIONS.MANAGE_SETTINGS },
    ],
  },
];

const PREFIX = '/call-centre/';

/** The guide has its own page; every other internal section is a data tab. */
export const GUIDE_HREF = '/call-centre/guide';
export const GUIDE_SEEN_KEY = 'callCentre.guideSeen';

/**
 * A section's menu identity — group label, icon and name — keyed BOTH ways.
 *
 * The Guide's answer index and the Methodology contents both want "which group
 * is this in, and what icon does the menu give it", and both had been about to
 * hand-write the answer. A second copy of this mapping is a copy that goes stale
 * the day a section moves group, in exactly the two places whose job is to tell
 * somebody where a section lives.
 *
 * Keyed by href (`/call-centre/now`) and by the bare slug (`now`), because the
 * Guide names destinations by address and the logic registry names them by key.
 *
 * Deliberately derived rather than declared: the nav guard tests read this file
 * as an AST and collect every object literal carrying an `href`, so a second
 * literal list here would register as a second menu.
 */
export const NAV_META = (() => {
  const map = new Map();
  for (const group of CALL_CENTRE_NAV) {
    for (const item of group.items) {
      const meta = { group: group.label, icon: item.icon, name: item.name };
      map.set(item.href, meta);
      map.set(item.href.replace('/call-centre/', ''), meta);
    }
  }
  return map;
})();

/** Every internal section key, the guide included. */
export const CALL_CENTRE_SECTIONS = CALL_CENTRE_NAV
  .flatMap((s) => s.items)
  .filter((i) => i.href.startsWith(PREFIX))
  .map((i) => i.href.slice(PREFIX.length));

/** The sections this user may see, empty groups dropped. */
export function visibleCallCentreNav(user) {
  return visibleSections(CALL_CENTRE_NAV, user);
}

const MY_WORK = new Set(['/call-centre/my-day', '/call-centre/my-roster', '/call-centre/my-stats', '/call-centre/my-followup']);

/**
 * Where `/call-centre` lands: the guide on a first visit; after that, My Day for
 * somebody who works leads (no team permission), and the desk for a team lead or
 * admin — so each person opens on the screen their morning starts with.
 */
export function firstCallCentreHref(user, { guideSeen = false } = {}) {
  const items = visibleCallCentreNav(user).flatMap((s) => s.items).filter((i) => i.href.startsWith(PREFIX));
  if (!guideSeen && items.some((i) => i.href === GUIDE_HREF)) return GUIDE_HREF;
  const seesTeam = navVisible({ permission: PERMISSIONS.VIEW_CALL_CENTRE_TEAM }, user);
  const personal = items.filter((i) => MY_WORK.has(i.href));
  if (!seesTeam && personal.length) return personal[0].href;
  const desk = items.find((i) => i.href !== GUIDE_HREF && !MY_WORK.has(i.href));
  return (desk || personal[0] || items[0])?.href || null;
}

/** The title for a path inside the window. */
export function callCentrePageTitle(pathname) {
  const all = CALL_CENTRE_NAV.flatMap((s) => s.items);
  const hit = all.find((i) => pathname === i.href || String(pathname || '').startsWith(`${i.href}/`));
  return hit?.name || '';
}
